import React, { useState, useMemo } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

export interface TabProps {
  index: number
  label: string
  count?: number
  content: React.ReactNode
}

interface StaticTabsComponentProps {
  tabs: TabProps[]
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  const theme = useTheme()

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: 1,
            backgroundColor: getThemeColor(theme, MUIThemeColors.white),
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export const StaticTabsComponent: React.FC<StaticTabsComponentProps> = ({ tabs }) => {
  const [value, setValue] = useState(0)
  const theme = useTheme()

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const renderedTabs = useMemo(
    () =>
      tabs.map((tab, index) => (
        <Tab
          key={tab.index ?? index}
          label={
            <Typography color={getThemeColor(theme, MUIThemeColors.black)} variant="body1" fontWeight={500}>
              {tab.label}
              {typeof tab.count === 'number' && <CountBadge>{tab.count}</CountBadge>}
            </Typography>
          }
          {...a11yProps(tab.index)}
          sx={{
            whiteSpace: 'nowrap',
            textTransform: 'none',
            color: `${getThemeColor(theme, MUIThemeColors.primaryMain)} !important`,
            '&.Mui-selected': {
              backgroundColor: getThemeColor(theme, MUIThemeColors.white),
            },
            maxWidth: 'none',
          }}
        />
      )),
    [tabs, theme],
  )

  const renderedPanels = useMemo(
    () =>
      tabs.map((tab, index) => (
        <CustomTabPanel value={value} key={tab.index ?? index} index={tab.index}>
          {tab.content}
        </CustomTabPanel>
      )),
    [tabs, value],
  )
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 4, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          TabIndicatorProps={{
            style: {
              height: '4px',
            },
          }}
          sx={{ position: 'relative', top: '4px' }}
        >
          {renderedTabs}
        </Tabs>
      </Box>
      {renderedPanels}
    </Box>
  )
}
export default StaticTabsComponent

export const CountBadge = styled('span')(({ theme }) => ({
  fontSize: '12px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  borderRadius: '4px',
  marginLeft: '6px',
  padding: '2px 6px',
}))
